/*
 * Copyright (c) Nexign, JSC, 2020-2025.
 */
/**
 * The component to list accounts for the widget Search Accounts
 *
 * @author Anton Panasenko
 * @author Aleksandr Perkhunov
 * @author Nikolay Sviridov
 * @version 1.36.0
 * @since 1.3.0
 */

import { CSSProperties, memo, RefObject, useEffect, useMemo, useRef, useState } from 'react';

import Highlighter from 'react-highlight-words';
import { connect, useSelector } from 'react-redux';
import { FixedSizeList } from 'react-window';
import { Dispatch } from 'redux';
import { createStructuredSelector } from 'reselect';

import { FeatureNameEnum } from '@/main/modules/role/constants/guard/guardConstants';
import { makeSelectFeatureEnabled } from '@/main/pages/PageAuth/selectors/authSelectors';

import { EllipseText } from '../../../../../../components/common';
import InfinityScrollWrapper from '../../../../../../components/InfinityScrollWrapper/InfinityScrollWrapper';
import { usePreventWindowScroll } from '../../../../../../modules/app/hooks/usePreventWindowScroll';
import IPageDashboardTranslation from '../../../../../../modules/internationalization/types/pages/dashboard/dashboard';
import { IRootReducer } from '../../../../../../reducers/rootReducer';
import { dashboard } from '../../../../../../theme';
import { makeSelectorWidgetMetaList } from '../../widgetSelectors';
import { EWidgetNames, TWidgetMetaList } from '../../widgetTypes';
import { IWidgetSearchAccountsLoadListPayload } from '../types/widgetSearchAccountsActionTypes';
import { EWidgetSearchAccountsField, IWidgetSearchAccountsItem } from '../types/widgetSearchAccountsTypes';
import {
    widgetSearchAccountsLoadList,
} from '../widgetSearchAccountsActions';
import { showFieldsDefault } from '../widgetSearchAccountsConstants';
import {
    makeSelectWidgetSearchAccountsHasMore,
    makeSelectWidgetSearchAccountsIsLoading,
    makeSelectWidgetSearchAccountsList,
    makeSelectWidgetSearchAccountsSearchValue,
    makeSelectWidgetSearchAccountsSelectedAccount,
} from '../widgetSearchAccountsSelectors';
import {
    WidgetSearchAccountsHeaderStyled,
    WidgetSearchAccountsItemStyled,
    WidgetSearchAccountsListBodyStyled,
    WidgetSearchAccountsListContainerStyled,
    WidgetSearchAccountsListItemStyled,
    WidgetSearchAccountsListRowStyled,
} from './styled/WidgetSearchAccountsStyled';

export interface IWidgetSearchAccountsListProps {
    onSelectAccount: (item: IWidgetSearchAccountsItem) => void;
    translation: IPageDashboardTranslation;
}

export interface IConnectedProps {
    accountList: IWidgetSearchAccountsItem[];
    hasMore: boolean;
    isLoading: boolean;
    searchValue: string;
    selectedAccount: IWidgetSearchAccountsItem;
    widgetMeta: TWidgetMetaList;
}

export interface IDispatchProps {
    loadAccountsList: (payload?: IWidgetSearchAccountsLoadListPayload) => void;
}

export type TWidgetSearchAccountsList = IWidgetSearchAccountsListProps & IConnectedProps & IDispatchProps;

export const WidgetSearchAccountsList = (props: TWidgetSearchAccountsList) => {

    const isMainOperationsEnabled = useSelector(makeSelectFeatureEnabled(FeatureNameEnum.MAIN_OPERATIONS));

    const [isListOverflown, setListOverflown] = useState(false);
    const listBodyRef: RefObject<HTMLDivElement> = useRef<HTMLDivElement>(null);

    const listMaxHeight: number = parseInt(dashboard.widgetSearchAccountsListHeight);
    const listCount: number = props.accountList.length;
    const rowHeight: number = parseInt(dashboard.widgetSearchAccountsRowHeight);
    const listCurrentHeight: number = listCount * rowHeight;
    const widgetSearchAccountsMeta = props.widgetMeta.find(widget => widget.name === EWidgetNames.SEARCH_ACCOUNTS);
    
    const showFields = useMemo(() => {
        if (isMainOperationsEnabled) {
            return widgetSearchAccountsMeta?.showFields || showFieldsDefault;
        } else {
            return showFieldsDefault
        }
    },[isMainOperationsEnabled, widgetSearchAccountsMeta?.showFields]);    

    usePreventWindowScroll(listBodyRef);

    useEffect(() => {
        setListOverflown(!!listBodyRef.current &&
            listBodyRef.current.scrollHeight > listBodyRef.current.clientHeight)
    }, [listBodyRef.current?.scrollHeight]);

    return (
        <WidgetSearchAccountsListContainerStyled>
            <WidgetSearchAccountsHeaderStyled>
                {
                    showFields.number &&
                    <WidgetSearchAccountsItemStyled
                        width={dashboard.widgetSearchAccountsListNumberWidth}
                    >
                        {props.translation.widgets.searchAccounts[EWidgetSearchAccountsField.number]}
                    </WidgetSearchAccountsItemStyled>
                }

                {
                    showFields.region && 
                    <WidgetSearchAccountsItemStyled>
                        {props.translation.widgets.searchAccounts[EWidgetSearchAccountsField.region]}
                    </WidgetSearchAccountsItemStyled>
                }

                {
                    showFields.name && 
                    <WidgetSearchAccountsItemStyled>
                        {props.translation.widgets.searchAccounts[EWidgetSearchAccountsField.name]}
                    </WidgetSearchAccountsItemStyled>
                }

                {
                    showFields.inn && 
                    <WidgetSearchAccountsItemStyled
                        textAlign='right'
                    >
                        {props.translation.widgets.searchAccounts[EWidgetSearchAccountsField.inn]}
                    </WidgetSearchAccountsItemStyled>
                }

                {
                    showFields.balance && 
                        <WidgetSearchAccountsItemStyled
                        textAlign='right'
                        width={dashboard.widgetSearchAccountsListBalanceWidth}
                    >
                        {props.translation.widgets.searchAccounts[EWidgetSearchAccountsField.balance]}
                    </WidgetSearchAccountsItemStyled>
                }

                {
                    showFields.subsCount && 
                    <WidgetSearchAccountsItemStyled
                        textAlign='right'
                        width={dashboard.widgetSearchAccountsListSubscribersWidth}
                    >
                        {props.translation.widgets.searchAccounts[EWidgetSearchAccountsField.subsCount]}
                    </WidgetSearchAccountsItemStyled>
                }
            </WidgetSearchAccountsHeaderStyled>

            <WidgetSearchAccountsListBodyStyled>
                <InfinityScrollWrapper
                    hasMore={props.hasMore}
                    isLoading={props.isLoading}
                    sizeElement={listBodyRef}
                    watchElement={listBodyRef}
                    onScrollEvent={() => props.loadAccountsList()}
                >
                    <FixedSizeList
                        height={listCurrentHeight > listMaxHeight ? listMaxHeight : listCurrentHeight}
                        itemCount={listCount}
                        itemSize={rowHeight}
                        outerRef={listBodyRef}
                        width={'100%'}
                    >
                        {
                            ({ index, style }: { index: number; style: CSSProperties }) => {
                                const item = props.accountList[index];
                                return (
                                    <WidgetSearchAccountsListRowStyled
                                        data-index={item.id}
                                        isPaddingRight={!isListOverflown}
                                        isSelectedAccount={item.id === props.selectedAccount.id}
                                        key={item.id}
                                        style={style}
                                        onClick={() => props.onSelectAccount(item)}
                                    >
                                        {
                                            showFields.number &&
                                            <WidgetSearchAccountsListItemStyled
                                                width={dashboard.widgetSearchAccountsListNumberWidth}
                                            >
                                                <Highlighter
                                                    autoEscape={true}
                                                    highlightClassName="widget-search-accounts__list-item--text-bold"
                                                    searchWords={[props.searchValue]}
                                                    textToHighlight={item[EWidgetSearchAccountsField.number]}
                                                />
                                            </WidgetSearchAccountsListItemStyled>
                                        }

                                        {
                                            showFields.region &&
                                            <EllipseText
                                                title={item[EWidgetSearchAccountsField.region]}
                                                wrapper={WidgetSearchAccountsListItemStyled}
                                            >
                                                <Highlighter
                                                    autoEscape={true}
                                                    highlightClassName="widget-search-accounts__list-item--text-bold"
                                                    searchWords={props.searchValue.split(' ')}
                                                    textToHighlight={item[EWidgetSearchAccountsField.region]}
                                                />
                                            </EllipseText>
                                        }

                                        {
                                            showFields.name &&
                                            <EllipseText
                                                title={item[EWidgetSearchAccountsField.name] ?? ''}
                                                wrapper={WidgetSearchAccountsListItemStyled}
                                            >
                                                <Highlighter
                                                    autoEscape={true}
                                                    highlightClassName="widget-search-accounts__list-item--text-bold"
                                                    searchWords={props.searchValue.split(' ')}
                                                    textToHighlight={item[EWidgetSearchAccountsField.name] ?? ''}
                                                />
                                            </EllipseText>
                                        }

                                        {
                                            showFields.inn &&
                                            <WidgetSearchAccountsListItemStyled
                                                textAlign='right'
                                                width={dashboard.widgetSearchAccountsListBalanceWidth}
                                            >
                                                {item[EWidgetSearchAccountsField.inn]}
                                            </WidgetSearchAccountsListItemStyled>
                                        }

                                        {
                                            showFields.balance &&
                                            <WidgetSearchAccountsListItemStyled
                                                textAlign='right'
                                                width={dashboard.widgetSearchAccountsListBalanceWidth}
                                            >
                                                {item[EWidgetSearchAccountsField.balance]}
                                            </WidgetSearchAccountsListItemStyled>
                                        }

                                        {
                                            showFields.subsCount &&
                                            <WidgetSearchAccountsListItemStyled
                                                textAlign='right'
                                                width={dashboard.widgetSearchAccountsListSubscribersWidth}
                                            >
                                                {item[EWidgetSearchAccountsField.subsCount]}
                                            </WidgetSearchAccountsListItemStyled>
                                        }
                                    </WidgetSearchAccountsListRowStyled>)
                            }
                        }
                    </FixedSizeList>
                </InfinityScrollWrapper>
            </WidgetSearchAccountsListBodyStyled>
        </WidgetSearchAccountsListContainerStyled>
    )
};

const mapStateToProps = createStructuredSelector<IRootReducer, IConnectedProps>({
    accountList: makeSelectWidgetSearchAccountsList(),
    hasMore: makeSelectWidgetSearchAccountsHasMore(),
    isLoading: makeSelectWidgetSearchAccountsIsLoading(),
    searchValue: makeSelectWidgetSearchAccountsSearchValue(),
    selectedAccount: makeSelectWidgetSearchAccountsSelectedAccount(),
    widgetMeta: makeSelectorWidgetMetaList(),
});

export const mapDispatchToProps = (dispatch: Dispatch): IDispatchProps => ({
    loadAccountsList: (payload) =>
        dispatch(widgetSearchAccountsLoadList(payload)),
});

export default connect(mapStateToProps, mapDispatchToProps)(memo(WidgetSearchAccountsList));
